import React, { useState } from 'react';

const ActionPanel = ({ actions, explanation }) => {
  const [expanded, setExpanded] = useState(true);
  const [checkedActions, setCheckedActions] = useState({});

  if (!actions || actions.length === 0) return null;

  const toggleAction = (index) => {
    setCheckedActions(prev => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">⚡</span>
          <span className="font-semibold text-slate-800">Actionable Closures</span>
          <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
            {actions.length} actions
          </span>
        </div>
        <span className="text-slate-400">{expanded ? '▼' : '▶'}</span>
      </button>
      
      {expanded && (
        <div className="px-6 pb-6 pt-2 border-t border-slate-100">
          {explanation && (
            <div className="mb-4 p-4 bg-slate-50 rounded-lg">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Clinical Summary
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed">
                {explanation}
              </p>
            </div>
          )}
          
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Recommended Actions
          </h4>
          <div className="space-y-2">
            {actions.map((action, idx) => {
              const actionText = typeof action === 'string' ? action : (action?.action || 'Unknown intervention');
              const urgency = typeof action === 'object' ? action?.urgency : null;
              const medication = typeof action === 'object' ? action?.medication : null;

              return (
                <div 
                  key={idx}
                  className={`flex items-start gap-3 p-3 rounded-lg transition ${
                    checkedActions[idx] 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-slate-50 hover:bg-slate-100'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleAction(idx)}
                    className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center transition flex-shrink-0 ${
                      checkedActions[idx]
                        ? 'bg-green-500 border-green-500 text-white'
                        : 'border-slate-300 hover:border-indigo-400'
                    }`}
                  >
                    {checkedActions[idx] && (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-medium ${checkedActions[idx] ? 'text-slate-500 line-through' : 'text-slate-700'}`}>
                        {actionText}
                      </span>
                      {urgency && (
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                          urgency === 'IMMEDIATE' ? 'bg-red-100 text-red-700' :
                          urgency === 'Within 1 hour' ? 'bg-orange-100 text-orange-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {urgency}
                        </span>
                      )}
                    </div>
                    {medication && (
                      <div className="text-xs text-slate-400 mt-0.5">
                        {medication}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="mt-4 flex items-center justify-between text-xs text-slate-400 border-t border-slate-100 pt-4">
            <span>
              {Object.values(checkedActions).filter(Boolean).length} of {actions.length} actions completed
            </span>
            <button type="button" className="text-indigo-600 hover:text-indigo-800 font-medium">
              Generate Care Plan →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionPanel;
